import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";
import { FieldArray, Formik } from "formik";
import addClientChatterSchema from "./addChatter.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { MinusIcon, PlusIcon } from "Assets/icons";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import { GENDER_OPTIONS, GRADE_OPTIONS, MIME_TYPES, DUPLICATE_CLIENT_CHECKS } from "config/constants";
import { generatePath, useNavigate, useParams, Link } from "react-router-dom";
import { CHATTER_CLIENT_LIST, VIEW_CHATTER_CLIENT } from "config/paths";
import { useChatterClientDetailsQuery, useCreateClientChatterMutation, useUpdateChatterClientMutation } from "store/services/clients/chatter.client";
import { cloneDeep } from "lodash";
import { errorMessageToast, successMessageToast } from "helpers/common";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { useClientsServiceStatusListQuery, useGetFundingSourceListQuery } from "store/services/utility";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import { useCreateNoteMutation, useGetNoteQuery } from "store/services/note";
import NotesComponent from "components/shared/FormComponents/NotesComponent";
import { useArchiveGuardianMutation } from "store/services/archive";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import LoadingOverlay from "components/shared/LoadingOverlay";
import GuardianPicker from "components/shared/Cllients/GuardianPicker";
import DuplicateClientModal from "components/Modals/DuplicateClientModal";
import { useCheckDuplicateClientMutation } from "store/services/client";
import moment from "moment";

const AddClientChatter = () => {

  const { id: clientId } = useParams();
  const editMode = !!clientId;

  const navigate = useNavigate();
  const [currentEntry, setCurrentEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [checkDuplicateClient, { isLoading: duplicateCheckLoading }] = useCheckDuplicateClientMutation();
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [pendingClientPayload, setPendingClientPayload] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  //  Service status lookup
  const { data: serviceStatusList, isLoading: serviceStatusListLoading } = useClientsServiceStatusListQuery({ module: "client_service", client_type: "chatter" });
  const serviceStatusOptions = serviceStatusList?.map(i => ({ value: i.id, label: i.title }));

  // Notes
  const [createNote] = useCreateNoteMutation();
  const { data: noteDetails } = useGetNoteQuery({ clientId, moduleName: "clients-chatter" }, { skip: !clientId });

  // Funding source filter list
  const { data: fundingSourceListData, isLoading: fundingSourceListLoading } = useGetFundingSourceListQuery();
  const fundingSourceList = fundingSourceListData?.map(item => ({ value: item?.id, label: item?.name }));

  const { data: clientDetails, isLoading } = clientId ? useChatterClientDetailsQuery(clientId) : {};
  const parents = clientDetails?.client_detail?.client_guardian_mapping?.map((guardian) => guardian?.client_guardian?.client_role?.slug == "parent" ? guardian?.client_guardian?.id : null)?.filter(Boolean);
  const teachers = clientDetails?.client_detail?.client_guardian_mapping?.map((guardian) => guardian?.client_guardian?.client_role?.slug == "teacher" ? guardian?.client_guardian?.id : null)?.filter(Boolean);
  const coordinators = clientDetails?.client_detail?.client_guardian_mapping?.map((guardian) => guardian?.client_guardian?.client_role?.slug == "coordinator" ? guardian?.client_guardian?.id : null)?.filter(Boolean);


  const [createClient, { isLoading: createClientLoading }] = useCreateClientChatterMutation();
  const [updateClient, { isLoading: updateClientLoading }] = useUpdateChatterClientMutation();
  const [archiveGuardian, { isLoading: archiveGuardianLoading }] = useArchiveGuardianMutation();

  const [initialValues, setInitialValues] = useState({
    lastname: "",
    firstname: "",
    dob: "",
    gender: "",
    funding_source: "",
    city_town: "",
    status: "active",
    documents: [],
    grade: "",
    // teachers: [{
    //   firstname: "",
    //   lastname: "",
    //   phone: "",
    //   email: ""
    // }],
    parents: [],
    teachers: [],
    coordinators: [],
    contracts: [],
    is_confirmed: "yes",
    note: "",
    // service_status: ""
  });

  const handleAttachDocument = (name, value, prevValues, setFieldValue) => {
    try {
      let cloneValues = cloneDeep(prevValues);
      cloneValues.push({
        attachment_key: value,
        attachment_name: value,
      })
      setFieldValue(name, cloneValues);
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  const handleGuardianArchive = async () => {
    const {parents, teachers, coordinators} = currentEntry?.values || {};

    if(parents?.length + teachers?.length + coordinators?.length == 1){
      errorMessageToast("At least one guardian is required. Please add another guardian before archiving this one.")
      setShowModal(false);
      return;
    }
    
    try {
      setShowModal(false);

      const { list = [], id, setFieldValue, fieldName } = currentEntry || {};

      const updatedList = list.filter(item => item?.id !== id);

      // ✅ Dynamically update correct field
      if (typeof setFieldValue === "function") {
        setFieldValue(fieldName, updatedList);
      }

      // API call (same for all types)
      const enterToDelete = clientDetails?.client_detail?.client_guardian_mapping?.find(
        x => x?.client_guardian?.id == id
      );

      const result = await archiveGuardian(enterToDelete?.id);

      if (result?.error) return;

      successMessageToast(result?.data?.message);

    } catch (err) {
      console.error("Error archive guardian", err);
    }
  };

  const handleSubmit = async (payload) => {

    if (!editMode && DUPLICATE_CLIENT_CHECKS.chatter) {
      try {
        const duplicateResult = await checkDuplicateClient({
          clientType: "chatter",
          data: {
            firstname: payload?.firstname?.trim(),
            lastname: payload?.lastname?.trim(),
            dob: moment(payload?.dob).format("YYYY-MM-DD"),
          },
        }).unwrap();
        const matches = duplicateResult?.data?.matches || [];

        if (matches.length) {
          setDuplicateMatches(matches);
          setPendingClientPayload(cloneDeep(payload));
          setShowDuplicateModal(true);
          return;
        }
      } catch (err) {
        console.error("Error checking for duplicate Chatter Client", err);
        return;
      }
    }

    await createOrUpdateClient(payload);
  };

  const createOrUpdateClient = async (payload) => {

    try {
      let result;
      let values = cloneDeep(payload);

      // format values
      delete values.note;
      values.funding_source = parseInt(payload?.funding_source?.value);
      values.gender = payload?.gender?.value;
      values.school_division = payload?.school_division?.value;
      values.school_profile = payload?.school_profile?.value;
      values.grade = values?.grade?.value
      // values.service_status = payload?.service_status?.value;

      // values.teachers = values.teachers?.map(row => ({
      //   ...row,
      //   lastname: row.lastname.label
      // }));

      values.parents = values.parents?.map(row => ({
        ...row,
        lastname: row.lastname.label
      }))

      values.teachers = values.teachers?.map(row => ({
        ...row,
        lastname: row.lastname.label
      }));

      values.coordinators = values.coordinators?.map(row => ({
        ...row,
        lastname: row.lastname.label
      }));

      if (editMode) {
        result = await updateClient({ data: values, clientId });
      } else {
        result = await createClient(values);
      }

      if (result?.error) {
        console.error("Error creating School Client", result.error);
      } else {

        if (payload?.note) await createNote({ module: "clients-chatter", client_id: result?.data?.data?.id, note: payload?.note })

        successMessageToast(result?.data?.message);
        navigate(CHATTER_CLIENT_LIST);
      }
    } catch (err) {
      console.error("Error creating School Client", err);
    }
  };

  const handleCreateAnyway = async () => {
    if (!pendingClientPayload) return;
    setShowDuplicateModal(false);
    await createOrUpdateClient(pendingClientPayload);
    setPendingClientPayload(null);
  };

  const handleViewDuplicateClient = (id) => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
    navigate(generatePath(VIEW_CHATTER_CLIENT, { id }));
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
  };

  useEffect(() => {

    if (!clientId) return;

    let teachers = [];
    let parents = [];
    let coordinators = [];
    clientDetails?.client_detail?.client_guardian_mapping?.length &&
      clientDetails?.client_detail?.client_guardian_mapping?.forEach((guardian) => {
        let { id, firstname, lastname, phone, email, client_role } = guardian?.client_guardian || {};
        let guardiandetails = {
          id,
          firstname,
          lastname: {
            value: id,
            label: lastname,
          },
          phone,
          email
        };
        if (client_role?.slug == "teacher") {
          teachers.push(guardiandetails);
        } else if (client_role?.slug == "parent") {
          parents.push(guardiandetails);
        } else if (client_role?.slug == "coordinator") {
          coordinators.push(guardiandetails);
        }
      })

    setInitialValues({
      ...initialValues,
      firstname: clientDetails?.firstname,
      lastname: clientDetails?.lastname,
      dob: clientDetails?.client_detail?.dob,
      gender: GENDER_OPTIONS.find(item => item.value === clientDetails?.client_detail?.gender),
      funding_source: fundingSourceList?.find(item => item.value === clientDetails?.client_detail?.client_chatter_detail?.funding_source),
      city_town: clientDetails?.client_detail?.city_town,
      grade: clientDetails?.client_detail?.grade ? { label: clientDetails?.client_detail?.grade, value: clientDetails?.client_detail?.grade } : "",
      // teachers: teachers,
      parents: parents?.length ? parents : [],
      teachers: teachers?.length ? teachers : [],
      coordinators: coordinators?.length ? coordinators : [],
      documents:
        clientDetails?.clientResources?.length ?
          clientDetails?.clientResources?.map(resource => resource.type == "attachment" && ({
            id: resource.id,
            attachment_key: resource.attachment_key,
            attachment_name: resource.attachment_name
          }))?.filter(Boolean) : [],
      status: clientDetails?.status,
      is_confirmed: clientDetails?.client_detail?.client_chatter_detail?.is_confirmed,
      // service_status: serviceStatusOptions?.find(x => x.value == clientDetails?.client_detail?.service_status) || "",
    })


  }, [clientDetails]);

  return (
    <>
      <ConfirmationPop
        confirmation={showModal}
        setConfirmation={setShowModal}
        type={"delete"}
        confirmHandler={handleGuardianArchive}
      />
      <DuplicateClientModal
        show={showDuplicateModal}
        matches={duplicateMatches}
        onCancel={handleCancelDuplicate}
        onCreateAnyway={handleCreateAnyway}
        onViewClient={handleViewDuplicateClient}
        isCreating={createClientLoading}
      />
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to={CHATTER_CLIENT_LIST}
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Client" : "Add New Client"}</h6>
              </div>
            </Col>
            {
              isLoading || fundingSourceListLoading || serviceStatusListLoading ? <LoadingIndicator /> :
                <Col lg="12" className="my-2">
                  {archiveGuardianLoading && <LoadingOverlay />}
                  <Formik
                    initialValues={initialValues}
                    validationSchema={addClientChatterSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({
                      handleSubmit,
                      isSubmitting,
                      values,
                      errors,
                      touched,
                      setFieldTouched,
                      setFieldValue,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        {console.error("Form Errors", touched)}
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Row>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Last Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="lastname"
                                type="text"
                                placeholder="e.g Smith"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                First Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="firstname"
                                type="text"
                                placeholder="e.g John"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                DOB
                                <RequiredFieldAstrick />
                              </label>
                              <AppDatePicker
                                name="dob"
                                maxDate={new Date()}
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Gender Assigned at Birth
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="gender"
                                placeholder={"Select Gender"}
                                option={GENDER_OPTIONS}
                              // cstmOption={CustomGenderOption}
                              />
                            </Col>

                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Grade
                              </label>
                              <CommonSelect
                                name="grade"
                                placeholder={"Please Select Grade"}
                                option={GRADE_OPTIONS}
                              // cstmOption={CustomGenderOption}
                              />
                            </Col>

                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Funding Source
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="funding_source"
                                placeholder={"Please Select Source"}
                                option={fundingSourceList}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                City/Town
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="city_town"
                                type="text"
                                placeholder="City/Town"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Status
                                <RequiredFieldAstrick />
                              </label>
                              <AppSelectField
                                name={"status"}
                                placeholder={"Please Select"}
                                className={`${styles.formControl} form-control`}
                                options={[
                                  {
                                    value: "active",
                                    label: "Active",
                                  },
                                  {
                                    value: "archived",
                                    label: "Archived",
                                  }
                                ]}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Confirmed
                                <RequiredFieldAstrick />
                              </label>
                              <AppSelectField
                                name={"is_confirmed"}
                                placeholder={"Please Select"}
                                className={`${styles.formControl} form-control`}
                                options={[
                                  {
                                    value: "yes",
                                    label: "Yes",
                                  },
                                  {
                                    value: "no",
                                    label: "No",
                                  }
                                ]}
                              />
                            </Col>
                            {/* <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Service Status
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="service_status"
                                placeholder={"Please Select Service Status"}
                                option={serviceStatusOptions}
                              />
                            </Col> */}
                            <Col md="12" sm="12" className="my-2">
                              {/* <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >

                                Notes
                              </label> */}
                              {
                                editMode ?
                                  <NotesComponent
                                    notes={noteDetails}
                                    className={`${styles.formControl} form-control`}
                                    module={"clients-chatter"}
                                    client_id={clientId}
                                  /> :
                                  <AppInputField
                                    name={`note`}
                                    placeholder="Notes"
                                    fieldType={"textarea"}
                                    maxLength={500}
                                    className={`${styles.formControl} form-control`}
                                  />
                              }
                            </Col>
                            {/* <Col md="12" sm="12" className="my-2">
                              <hr />
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex my-2"
                                style={{ fontSize: "15px" }}
                              >
                                Teacher's Details
                              </label>
                              <FieldArray name="teachers">
                                {({ insert, remove, push }) => (
                                  <ul className="list-unstyled ps-0 mb-0">
                                    {values.teachers.length > 0 &&
                                      values.teachers.map((area, index) => {
                                        let isLastItem = index === values.teachers.length - 1;
                                        return (
                                          <Row className="align-items-center" key={index}>
                                            <Col lg="10">
                                              <Row>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Last Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <CommonCreateSelect
                                                    placeholder={"Search Last Name"}
                                                    handleBlur={() => setTeachersList([])}
                                                    name={`teachers.${index}.lastname`}
                                                    creatable={true}
                                                    onInputChange={(e) => handleSearch(e, "teachers")}
                                                    option={teachersList}
                                                    isLoading={getTeachersLoading}
                                                    onChange={(selected) => {

                                                      let teacher = teachersList?.find(teacher => teacher.id === selected?.value);
                                                      if (!teacher) {
                                                        setFieldValue(`teachers.${index}.lastname`, selected);
                                                        return;
                                                      }

                                                      setFieldValue(`teachers.${index}.id`, teacher.id);
                                                      setFieldValue(`teachers.${index}.firstname`, teacher?.firstname);
                                                      setFieldValue(`teachers.${index}.email`, teacher?.email);
                                                      setFieldValue(`teachers.${index}.phone`, teacher?.phone);
                                                      setTeachersList([]);
                                                    }}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    First Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    name={`teachers.${index}.firstname`}
                                                    placeholder="e.g John Smith"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Email
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    name={`teachers.${index}.email`}
                                                    placeholder="John@gmail.com"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Phone
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    name={`teachers.${index}.phone`}
                                                    placeholder="+1234567890"
                                                    className={`${styles.formControl} form-control`}
                                                    maxLength={25}
                                                  />
                                                </Col>
                                              </Row>
                                            </Col>
                                            <Col lg="2">
                                              <Button
                                                className="border-0 p-0"
                                                variant="transparnet"
                                              >
                                                {isLastItem ? (

                                                  values.teachers.length > 1 ? (
                                                    <>
                                                      <PlusIcon onClick={() => push({ ...singleTeacher })} />
                                                      <MinusIcon onClick={() => remove(index)} />
                                                    </>
                                                  ) : <PlusIcon onClick={() => push({ ...singleTeacher })} />

                                                ) : <MinusIcon onClick={() => remove(index)} />
                                                }
                                              </Button>
                                            </Col>
                                          </Row>
                                        )
                                      })}
                                  </ul>
                                )}
                              </FieldArray>
                            </Col> */}
                            <Col md="12" sm="12" className="my-2">
                              {/* <hr /> */}
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex my-2"
                                style={{ fontSize: "15px" }}
                              >
                                Parent / Guardian Details
                              </label>
                              <FieldArray name="parents">
                                {({ insert, remove, push }) => (
                                  <>
                                    <ul className="list-unstyled ps-0 mb-0">
                                      {values.parents.length > 0 &&
                                        values.parents.map((area, index) => {
                                          let isLastItem = index === values.parents.length - 1;
                                          return (
                                            <Row className="align-items-center" key={index}>
                                              <Col lg="10">
                                                <Row>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Last Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <GuardianPicker
                                                      index={index}
                                                      type="parents"
                                                      name={`parents.${index}.lastname`}
                                                      placeholder={"Search Last Name"}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      First Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`parents.${index}.firstname`}
                                                      placeholder="e.g John Smith"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Email
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`parents.${index}.email`}
                                                      placeholder="John@gmail.com"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Phone
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`parents.${index}.phone`}
                                                      placeholder="+1234567890"
                                                      className={`${styles.formControl} form-control`}
                                                      maxLength={25}
                                                    />
                                                  </Col>
                                                </Row>
                                              </Col>
                                              {
                                                index <= 1 &&
                                                <Col lg="2">
                                                  <Button
                                                    className="border-0 p-0"
                                                    variant="transparnet"
                                                  >
                                                    {isLastItem ? (

                                                      values.parents.length >= 1 ? (
                                                        <>
                                                          {
                                                            index < 1 && <PlusIcon onClick={() => push({ ...singleParent })} />
                                                          }
                                                          <MinusIcon onClick={() => {
                                                            if (area.id && parents.includes(area.id)) {
                                                              setCurrentEntry({
                                                                ...area,
                                                                setFieldValue,
                                                                list: values?.parents,
                                                                fieldName: "parents",
                                                                values
                                                              }); setShowModal(true);
                                                              return;
                                                            }
                                                            remove(index)
                                                          }} />
                                                        </>
                                                      ) : <PlusIcon onClick={() => push({ ...singleParent })} />

                                                    ) : <MinusIcon onClick={() => {
                                                      if (area.id && parents.includes(area.id)) {
                                                        setCurrentEntry({
                                                          ...area,
                                                          setFieldValue,
                                                          list: values?.parents,
                                                          fieldName: "parents",
                                                          values
                                                        });
                                                        setShowModal(true);
                                                        return;
                                                      }
                                                      remove(index)
                                                    }} />
                                                    }
                                                  </Button>
                                                </Col>
                                              }
                                            </Row>
                                          )
                                        })}
                                    </ul>
                                    {
                                      values?.parents?.length == 0 &&
                                      <PlusIcon onClick={() => push({ ...singleParent })} />
                                    }
                                  </>
                                )}
                              </FieldArray>
                              {/* <hr /> */}
                            </Col>
                            <Col md="12" sm="12" className="my-0">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 mt-2"
                              >
                                Teacher's Details
                              </label>
                              <FieldArray name="teachers">
                                {({ insert, remove, push }) => (
                                  <>
                                    <ul className="list-unstyled ps-0 mb-0">
                                      {values.teachers.length > 0 &&
                                        values.teachers.map((area, index) => {
                                          let isLastItem = index === values.teachers.length - 1;
                                          return (
                                            <Row className="align-items-center" key={index}>
                                              <Col lg="10">
                                                <Row>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Last Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <GuardianPicker
                                                      index={index}
                                                      type="teachers"
                                                      name={`teachers.${index}.lastname`}
                                                      placeholder={"Search Last Name"}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      First Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`teachers.${index}.firstname`}
                                                      placeholder="e.g John Smith"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Email
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`teachers.${index}.email`}
                                                      placeholder="John@gmail.com"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Phone
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`teachers.${index}.phone`}
                                                      placeholder="+1234567890"
                                                      className={`${styles.formControl} form-control`}
                                                      maxLength={25}
                                                    />
                                                  </Col>
                                                </Row>
                                              </Col>
                                              <Col lg="2">
                                                <Button
                                                  className="border-0 p-0"
                                                  variant="transparnet"
                                                  disabled={archiveGuardianLoading}
                                                >
                                                  {true ? (

                                                    values?.teachers?.length >= 1 ? (
                                                      <>
                                                        {/* <PlusIcon onClick={() => push({ ...singleTeacher })} /> */}
                                                        <MinusIcon onClick={() => {
                                                          if (area.id && teachers.includes(area.id)) {
                                                            setCurrentEntry({
                                                              ...area,
                                                              setFieldValue,
                                                              list: values?.teachers,
                                                              fieldName: "teachers",
                                                              values
                                                            }); setShowModal(true);
                                                            return;
                                                          }
                                                          else remove(index)
                                                        }} />
                                                      </>
                                                    ) : <PlusIcon onClick={() => push({ ...singleTeacher })} />

                                                  ) : <MinusIcon onClick={() => {

                                                    if (area.id && teachers.includes(area.id)) {
                                                      setCurrentEntry({
                                                        ...area,
                                                        setFieldValue,
                                                        list: values?.teachers,
                                                        fieldName: "teachers",
                                                        values
                                                      }); setShowModal(true);
                                                      return;
                                                    }
                                                    else remove(index)
                                                  }} />
                                                  }
                                                </Button>
                                              </Col>
                                              {/* <Col lg="2">
                                                <MinusIcon onClick={() => {

                                                  if (area.id && teachers.includes(area.id)) {
                                                    setCurrentEntry({ ...area, setFieldValue, list: values?.teachers });
                                                    setShowModal(true);
                                                    return;
                                                  }
                                                  else remove(index)
                                                }} />
                                              </Col> */}
                                            </Row>
                                          )
                                        })}
                                    </ul>
                                    {
                                      values?.teachers?.length == 0 &&
                                      <PlusIcon onClick={() => push({ ...singleTeacher })} />
                                    }
                                  </>
                                )}
                              </FieldArray>
                            </Col>

                            <Col md="12" sm="12" className="my-0">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 mt-2"
                              >
                                Coordinator's Details
                              </label>
                              <FieldArray name="coordinators">
                                {({ insert, remove, push }) => (
                                  <>
                                    <ul className="list-unstyled ps-0 mb-0">
                                      {values?.coordinators.length > 0 &&
                                        values?.coordinators.map((area, index) => {
                                          let isLastItem = index === values?.coordinators.length - 1;
                                          return (
                                            <Row className="align-items-center" key={index}>
                                              <Col lg="10">
                                                <Row>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Last Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <GuardianPicker
                                                      index={index}
                                                      type="coordinators"
                                                      name={`coordinators.${index}.lastname`}
                                                      placeholder={"Search Last Name"}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      First Name
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`coordinators.${index}.firstname`}
                                                      placeholder="e.g John Smith"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Email
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`coordinators.${index}.email`}
                                                      placeholder="John@gmail.com"
                                                      className={`${styles.formControl} form-control`}
                                                    />
                                                  </Col>
                                                  <Col md="3" sm="6" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Phone
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppInputField
                                                      disabled={!!area?.id}
                                                      name={`coordinators.${index}.phone`}
                                                      placeholder="+1234567890"
                                                      className={`${styles.formControl} form-control`}
                                                      maxLength={25}
                                                    />
                                                  </Col>
                                                </Row>
                                              </Col>
                                              {/* <Col lg="2">
                                              <Button
                                                className="border-0 p-0"
                                                variant="transparnet"
                                                disabled={archiveGuardianLoading}
                                              >
                                                {isLastItem ? (

                                                  values?.coordinators?.length > 1 ? (
                                                    <>
                                                      <PlusIcon onClick={() => push({ ...singleCoordinator })} />
                                                      <MinusIcon onClick={() => {
                                                        if (area.id && coordinators.includes(area.id)) {
                                                          setCurrentItem({ ...area, setFieldValue, list: values?.coordinators });
                                                          setShowModal(true);
                                                          return;
                                                        }
                                                        else remove(index)
                                                      }} />
                                                    </>
                                                  ) : <PlusIcon onClick={() => push({ ...singleCoordinator })} />

                                                ) : <MinusIcon onClick={() => {

                                                  if (area.id && coordinators.includes(area.id)) {
                                                    setCurrentItem({ ...area, setFieldValue, list: values?.coordinators });
                                                    setShowModal(true);
                                                    return;
                                                  }
                                                  else remove(index)
                                                }} />
                                                }
                                              </Button>
                                            </Col> */}
                                              <Col lg="2">
                                                <MinusIcon onClick={() => {

                                                  if (area.id && coordinators.includes(area.id)) {
                                                    setCurrentEntry({
                                                      ...area,
                                                      setFieldValue,
                                                      list: values?.coordinators  ,
                                                      fieldName: "coordinators",
                                                      values
                                                    }); 
                                                    setShowModal(true);
                                                    return;
                                                  }
                                                  else remove(index)
                                                }} />
                                              </Col>
                                            </Row>
                                          )
                                        })}
                                    </ul>
                                    {
                                      values?.coordinators?.length == 0 &&
                                      <PlusIcon onClick={() => push({ ...singleCoordinator })} />
                                    }
                                  </>
                                )}
                              </FieldArray>
                            </Col>
                            {/* <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Upload Contracts
                              </label>
                              <AppFileUploadField
                                name="contracts"
                                acl={"private"}
                                accept="image/*, application/pdf, .doc, .docx"
                                allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.webp, MIME_TYPES.doc, MIME_TYPES.docx]}
                                className={`${styles.formControl} form-control`}
                                onChange={(name, value) => {
                                  handleAttachDocument(name, value, values?.contracts, setFieldValue);
                                }}
                              />
                            </Col>

                            <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Attach Documents
                              </label>
                              <AppFileUploadField
                                name="documents"
                                acl={"private"}
                                accept="application/pdf"
                                allowedTypes={[MIME_TYPES.pdf]}
                                className={`${styles.formControl} form-control`}
                                onChange={(name, value) => {
                                  handleAttachDocument(name, value, values?.documents, setFieldValue);
                                }}
                              />
                            </Col> */}
                            <Col lg="12">
                              {
                                errors?.guardians && (
                                  <div style={{ color: "#2a4ca9", fontSize: "15px", marginTop: "10px" }}>
                                    {errors.guardians}
                                  </div>
                                )
                              }
                            </Col>
                          </Row>
                        </div>
                        <Row className="mt-4">
                          <Col className="my-2" lg="12">
                            <div
                              className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                              style={{ borderTop: "1px solid #41a2a84d" }}
                            >
                              <Button
                                onClick={() => setFieldTouched('guardians')}
                                disabled={createClientLoading || updateClientLoading || duplicateCheckLoading} type="submit" className="d-flex align-items-center justify-content-center commonBtn">
                                {createClientLoading || updateClientLoading || duplicateCheckLoading ? <LoadingIndicator inline /> : "Save"}
                              </Button>

                              <Button
                                type="button"
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={() => navigate(CHATTER_CLIENT_LIST)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </Col>
            }
          </Row>
        </Container>
      </section>
    </>
  );
};

const singleParent = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
};

const singleTeacher = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
};

const singleCoordinator = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
};

export default AddClientChatter;
